// @flow
import React, { PureComponent } from 'react';
import { Link, withRouter } from 'react-router-dom';

import SelectInput from 'components/fragments/input/SelectInput';
import { FORM_BY_DEFAULT, LINK_BY_DEFAULT, PATH_TO_FORMS } from 'constants/contactForm7Constants';
import type { ContactFormType } from 'types/ContactForm7';
import { dynamicClassName } from 'utils/dynamicClassName';

export type StateProps = {
  contactFormList: Array<ContactFormType>
};

export type DispatchProps = {
  fetchAllContactForms: () => void
};

type Props = StateProps &
  DispatchProps & {
    customSelect?: string,
    formId: number,
    history: any,
    otherObjects?: any[],
    setCurrentThemeTitle: (currentThemeTitle: string) => void,
    slug: string,
    updateFormIdState: (formId: number) => void
  };

type State = {
  isOpen: boolean
};

class CF7FormObjectSelector extends PureComponent<Props, State> {
  _FormObjectSelector: ?HTMLElement;

  static defaultProps = {
    otherObjects: []
  };

  state: State = {
    isOpen: false
  };

  componentDidMount() {
    const { contactFormList, fetchAllContactForms } = this.props;

    //* fetch necessary data
    if (contactFormList.length === 0) {
      fetchAllContactForms();
    }

    this.updateFormId();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (prevState.isOpen === this.state.isOpen) {
      this.setState({
        isOpen: false
      });
    }

    if (prevProps.formId === -1 || prevProps.slug !== this.props.slug) {
      this.updateFormId();
    }
  }

  getObjectList = () => {
    const { contactFormList, otherObjects } = this.props;

    /** create an object list with the form by default */
    let objectList = contactFormList.map(form => {
      if (form.slug === FORM_BY_DEFAULT) {
        return { title: form.title, slug: LINK_BY_DEFAULT };
      }
      return { title: form.title, slug: form.slug };
    });

    /** add otherObjects only if they exists and a form with the same slug doesn't exist */
    if (otherObjects) {
      const otherObjectsObject = otherObjects.reduce((acc, theme): any[] => {
        const formAlreadyExists = contactFormList.find(form => {
          return form.slug === theme.slug;
        });

        if (formAlreadyExists) {
          return acc;
        }

        const themeToAdd = {
          title: theme.name,
          slug: theme.slug
        };

        return acc.concat(themeToAdd);
      }, []);

      objectList = objectList
        .concat(otherObjectsObject)
        .sort((a, b) => (a.title > b.title ? 1 : a.title < b.title ? -1 : 0));
    }

    return objectList;
  };

  getSelectedLabel = (objectList: { title: string, slug: string }[]) => {
    const { setCurrentThemeTitle, slug } = this.props;

    let label = 'Choisir un sujet';

    if (slug) {
      const formSelected = objectList.find(item => item.slug === slug) ?? false;

      if (formSelected) {
        label = formSelected.title;
      }
    } else {
      const formByDefault = objectList.find(item => item.slug === LINK_BY_DEFAULT);

      label = formByDefault ? formByDefault.title : LINK_BY_DEFAULT;
    }

    setCurrentThemeTitle(label);

    return label;
  };

  handleClickDropDown = (e: MouseEvent) => {
    e.preventDefault();

    if (!this.state.isOpen) {
      document.addEventListener('mousedown', this.handleClickOutside, { once: true });
    }

    this.setState(prevState => ({
      isOpen: !prevState.isOpen
    }));
  };

  handleClickOutside = (event: MouseEvent) => {
    if (this._FormObjectSelector && !this._FormObjectSelector.contains((event.target: any))) {
      this.handleClickDropDown(event);
    }
  };

  updateFormId = (value?: string) => {
    const { contactFormList, customSelect, history, slug, updateFormIdState } = this.props;

    // check if a form exists, otherwise the form by default is displayed
    const currentContactForm = contactFormList.find(form => form.slug === slug)
      ? contactFormList.find(form => form.slug === slug)
      : contactFormList.find(form => form.slug === FORM_BY_DEFAULT);

    if (currentContactForm) {
      updateFormIdState(currentContactForm.id);
    }

    if (!!customSelect && value) {
      history.push(`${PATH_TO_FORMS}${value}`);
    }
  };

  render() {
    const { contactFormList, customSelect, otherObjects, slug } = this.props;
    const { isOpen } = this.state;

    if (!contactFormList.length && otherObjects && !otherObjects.length) {
      return null;
    }

    // object list
    const objectList = this.getObjectList();

    // label title
    const label = this.getSelectedLabel(objectList);

    if (customSelect === 'SelectInput') {
      return (
        <SelectInput
          isLight
          placeholder="Objet de ma demande"
          options={objectList ? objectList.map(theme => ({ label: theme.title, value: theme.slug })) : []}
          onChange={this.updateFormId}
          errorMessage={'Ce champ est requis'}
          value={slug ?? ''}
        />
      );
    }

    // dropdown
    const classNames = dynamicClassName('dropdown dropdown--contact mb-4');
    isOpen && classNames.add('is-open');

    return (
      <div className={classNames.build()}>
        <span
          ref={ref => (this._FormObjectSelector = ref)}
          className="dropdown__head ft-h6 ft-up"
          role="button"
          tabIndex={0}
          onClick={this.handleClickDropDown}
        >
          <span className="dropdown__label">{label}</span>
        </span>
        <div className="dropdown__list">
          <span className="dropdown__head ft-h6 ft-up" role="menuitem" tabIndex={0} onClick={this.handleClickDropDown}>
            <span className="dropdown__label">{label}</span>
          </span>
          <div className="dropdown__wrapper">
            {/* Form from CF7 */}
            {objectList &&
              objectList.map((topic, index) => {
                return (
                  <Link
                    key={index}
                    title={topic.title}
                    to={`${PATH_TO_FORMS}${topic.slug}`}
                    onClick={this.updateFormId}
                  >
                    {topic.title}
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
    );
  }
}

export default withRouter(CF7FormObjectSelector);
